'use client';

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocalizedNavigate } from '@/components/i18n';
import { useContainerStatus } from '@/hooks/useContainerStatus';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Loader2, Sparkles, Brain, Shield } from 'lucide-react';

/** Routes that require the AI container to be ready */
const AI_ROUTES = ['/ask-sage', '/sibyl', '/strategy'];

/**
 * Returns true if the current pathname matches an AI feature route.
 * Strips locale prefix before checking.
 */
function isAIRoute(pathname: string): boolean {
  // Strip locale prefix (e.g., /es/ask-sage -> /ask-sage)
  const cleaned = pathname.replace(/^\/(en|es|zh)\//, '/');
  return AI_ROUTES.some((route) => cleaned.startsWith(route));
}

/** Maps boot phases to human-readable labels */
function getPhaseLabel(
  bootStatus: string | null,
  tablesSync: number,
): string {
  switch (bootStatus) {
    case 'cold_start':
      return 'Waking up your AI team...';
    case 'warming':
      return 'Loading AI models...';
    case 'syncing_tables':
      return `Syncing your case data (${Math.round(tablesSync * 100)}%)...`;
    case 'syncing_files':
      return 'Loading your documents...';
    case 'ready':
      return 'Ready!';
    case 'error':
      return 'Something went wrong';
    default:
      return 'Preparing your AI team...';
  }
}

/** Computes an approximate progress percentage from boot status */
function getProgressValue(
  bootStatus: string | null,
  tablesSync: number,
): number {
  switch (bootStatus) {
    case 'cold_start':
      return 10;
    case 'warming':
      return 25;
    case 'syncing_tables':
      // Tables sync maps to 30-70%
      return 30 + Math.round(tablesSync * 40);
    case 'syncing_files':
      return 80;
    case 'ready':
      return 100;
    default:
      return 5;
  }
}

const TEAM_MEMBERS = [
  { icon: Brain, name: 'Sage', role: 'AI Legal Strategist' },
  { icon: Sparkles, name: 'Sibyl', role: 'Financial Analyst' },
  { icon: Shield, name: 'Strategy Engine', role: 'Settlement Optimizer' },
];

/**
 * Loading splash overlay shown when a returning user navigates to an AI feature
 * while the container is still booting (typically 5-10 seconds).
 *
 * - Shows only when container is booting AND user is on an AI route
 * - Auto-dismisses when isReady = true
 * - "Skip" link falls back to legacy (non-container) processing
 */
export function ContainerBootSplash() {
  const location = useLocation();
  const navigate = useLocalizedNavigate();
  const { isBooting, isReady, isError, isEnabled, bootStatus, tablesSync } =
    useContainerStatus();

  const [dismissed, setDismissed] = useState(false);
  const [showContent, setShowContent] = useState(false);

  const onAIRoute = isAIRoute(location.pathname);
  const shouldShow = isEnabled && isBooting && onAIRoute && !dismissed;

  // Animate content in after mount
  useEffect(() => {
    if (shouldShow) {
      const timer = setTimeout(() => setShowContent(true), 50);
      return () => clearTimeout(timer);
    }
    setShowContent(false);
  }, [shouldShow]);

  // Auto-dismiss when ready
  useEffect(() => {
    if (isReady || isError) {
      // Brief delay so user sees "Ready!" state
      const timer = setTimeout(() => {
        setDismissed(true);
      }, isReady ? 600 : 0);
      return () => clearTimeout(timer);
    }
  }, [isReady, isError]);

  // Reset dismissed state when navigating away from AI routes
  useEffect(() => {
    if (!onAIRoute) {
      setDismissed(false);
    }
  }, [onAIRoute]);

  if (!shouldShow) {
    return null;
  }

  const progress = getProgressValue(bootStatus, tablesSync);
  const phaseLabel = getPhaseLabel(bootStatus, tablesSync);

  const handleSkip = () => {
    setDismissed(true);
  };

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-white/95 dark:bg-slate-950/95 backdrop-blur-sm transition-opacity duration-300',
        showContent ? 'opacity-100' : 'opacity-0',
      )}
      role="dialog"
      aria-label="AI container loading"
    >
      <div
        className={cn(
          'w-full max-w-md mx-auto px-6 text-center transition-all duration-500',
          showContent ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
        )}
      >
        {/* Animated icon */}
        <div className="mb-8">
          <div className="relative inline-flex items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-amber-400/20 animate-ping" />
            <div className="relative rounded-full bg-amber-100 p-4">
              <Loader2 className="h-8 w-8 text-amber-600 animate-spin" />
            </div>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-cream mb-2">
          Preparing your AI team...
        </h2>

        {/* Phase label */}
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
          {phaseLabel}
        </p>

        {/* Progress bar */}
        <div className="mb-6">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-slate-400 mt-2">{progress}%</p>
        </div>

        {/* Team member cards */}
        <div className="flex justify-center gap-4 mb-8">
          {TEAM_MEMBERS.map((member, i) => {
            const Icon = member.icon;
            // Stagger "activation" as progress increases
            const isActive = progress > (i + 1) * 25;
            return (
              <div
                key={member.name}
                className={cn(
                  'flex flex-col items-center gap-1 transition-all duration-500',
                  isActive ? 'opacity-100 scale-100' : 'opacity-40 scale-95',
                )}
              >
                <div
                  className={cn(
                    'rounded-full p-2.5 transition-colors duration-300',
                    isActive
                      ? 'bg-navy/10 text-navy dark:bg-cream/10 dark:text-cream'
                      : 'bg-slate-100 text-slate-400',
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  {member.name}
                </span>
                <span className="text-[10px] text-slate-400">{member.role}</span>
              </div>
            );
          })}
        </div>

        {/* Skip link */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleSkip}
          className="text-slate-400 hover:text-slate-600 text-xs"
        >
          Skip and use standard processing
        </Button>
      </div>
    </div>
  );
}

export default ContainerBootSplash;
